import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Plus, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

const SAMPLE_CSV = `address,price,bedrooms,bathrooms,type
"42 Sample Street, Sydney NSW 2000","$850,000",3,2,house "7/15 Example Rd, Melbourne VIC 3000","$620,000",2,1,unit`;

const BulkUploadModal = ({ onClose, onImport }) => {
  const { t } = useLanguage();
  const [tab, setTab] = useState('csv');
  const [csvText, setCsvText] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [manualRows, setManualRows] = useState([{ address: '', price: '', bedrooms: '', type: 'house' }]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const parseCSV = (text) => {
    try {
      const lines = text?.trim()?.split('\n');
      if (lines?.length < 2) { setParseError('CSV must have a header row and at least one data row.'); return; }
      const headers = lines?.[0]?.split(',')?.map(h => h?.replace(/"/g, '')?.trim()?.toLowerCase());
      const rows = lines?.slice(1)?.map(line => {
        const vals = line?.match(/(?:"([^"]*)"|([^,]+))/g)?.map(v => v?.replace(/"/g, '')?.trim()) || [];
        const obj = {};
        headers?.forEach((h, i) => { obj[h] = vals?.[i] || ''; });
        return obj;
      })?.filter(r => r?.address);
      setParsedRows(rows);
      setParseError('');
    } catch (e) {
      setParseError('Failed to parse CSV. Please check the format.');
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev?.target?.result || '';
      setCsvText(text);
      parseCSV(text);
    };
    reader?.readAsText(file);
  };

  const handleCSVChange = (text) => {
    setCsvText(text);
    if (text?.trim()) parseCSV(text);
    else { setParsedRows([]); setParseError(''); }
  };

  const addManualRow = () => setManualRows(prev => [...prev, { address: '', price: '', bedrooms: '', type: 'house' }]);
  const updateManualRow = (i, field, value) => setManualRows(prev => prev?.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const removeManualRow = (i) => setManualRows(prev => prev?.filter((_, idx) => idx !== i));

  const handleImport = () => {
    setImporting(true);
    const data = tab === 'csv' ? parsedRows : manualRows?.filter(r => r?.address?.trim());
    setTimeout(() => { onImport(data); setImporting(false); }, 800);
  };

  const canImport = tab === 'csv' ? parsedRows?.length > 0 : manualRows?.some(r => r?.address?.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden" style={{
        background: '#0A1628',
        border: '1px solid rgba(0,212,255,0.25)',
        boxShadow: '0 0 60px rgba(0,212,255,0.15)',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="flex items-center justify-between p-6" style={{ borderBottom: '1px solid rgba(0,212,255,0.12)' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#E8F4FD' }}>{t('bulkUploadTitle')}</h2>
            <p className="text-xs mt-0.5" style={{ color: '#4A7A9B' }}>{t('bulkUploadDesc')}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-all" style={{ color: '#4A7A9B' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#E8F4FD'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#4A7A9B'; e.currentTarget.style.background = 'transparent'; }}
          ><X size={18} /></button>
        </div>

        <div className="flex gap-1 p-4 pb-0">
          {[['csv', t('csvUpload')], ['manual', t('manualEntry')]]?.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className="px-4 py-2 rounded-t-lg text-sm font-semibold transition-all"
              style={{
                background: tab === key ? 'rgba(0,212,255,0.12)' : 'transparent',
                color: tab === key ? '#00D4FF' : '#4A7A9B',
                borderBottom: tab === key ? '2px solid #00D4FF' : '2px solid transparent'
              }}
            >{label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'csv' ? (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all"
                style={{ borderColor: 'rgba(0,212,255,0.25)', background: 'rgba(0,212,255,0.03)' }}
                onClick={() => fileRef?.current?.click()}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,212,255,0.5)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)'}
              >
                <Upload size={28} color="#00D4FF" className="mx-auto mb-3" />
                <p className="text-sm font-medium" style={{ color: '#E8F4FD' }}>{t('dropCSVHere')}</p>
                <p className="text-xs mt-1" style={{ color: '#4A7A9B' }}>{t('supportsCSV')}</p>
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold" style={{ color: '#6B9EC4' }}>{t('orPasteCSV')}</label>
                  <button onClick={() => handleCSVChange(SAMPLE_CSV)} className="text-xs" style={{ color: '#00D4FF' }}>{t('loadSample')}</button>
                </div>
                <textarea
                  value={csvText}
                  onChange={e => handleCSVChange(e?.target?.value)}
                  rows={5}
                  placeholder="address,price,bedrooms,bathrooms,type"
                  className="w-full rounded-xl p-3 text-xs font-mono outline-none resize-none"
                  style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)', color: '#A0C4D8' }}
                />
              </div>

              {parseError && (
                <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.25)' }}>
                  <AlertCircle size={14} color="#FF5050" />
                  <span className="text-xs" style={{ color: '#FF5050' }}>{parseError}</span>
                </div>
              )}
              {parsedRows?.length > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}>
                  <CheckCircle size={14} color="#00FF88" />
                  <span className="text-xs" style={{ color: '#00FF88' }}>{parsedRows?.length} {t('propertiesReadyToImport')}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {manualRows?.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <input type="text" placeholder={`${t('propertyAddress')} *`} value={row?.address}
                      onChange={e => updateManualRow(i, 'address', e?.target?.value)}
                      className="col-span-2 px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
                    />
                    <input type="text" placeholder={t('askingPrice')} value={row?.price}
                      onChange={e => updateManualRow(i, 'price', e?.target?.value)}
                      className="px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
                    />
                    <select value={row?.type} onChange={e => updateManualRow(i, 'type', e?.target?.value)}
                      className="px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
                    >
                      <option value="house">{t('house')}</option>
                      <option value="unit">Unit/Apartment</option>
                      <option value="commercial">{t('commercialLabel')}</option>
                      <option value="land">Land</option>
                    </select>
                  </div>
                  {manualRows?.length > 1 && (
                    <button onClick={() => removeManualRow(i)} className="p-2 rounded-lg flex-shrink-0" style={{ color: '#FF5050', background: 'rgba(255,80,80,0.1)' }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addManualRow} className="flex items-center gap-2 text-sm" style={{ color: '#00D4FF' }}>
                <Plus size={15} />
                {t('addAnotherProperty')}
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6" style={{ borderTop: '1px solid rgba(0,212,255,0.12)' }}>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{ color: '#6B9EC4', border: '1px solid rgba(0,212,255,0.2)' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,212,255,0.2)'}
          >{t('cancel')}</button>
          <button
            onClick={handleImport}
            disabled={!canImport || importing}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: canImport ? 'linear-gradient(135deg, #00D4FF, #7B2FFF)' : 'rgba(0,212,255,0.1)',
              color: canImport ? '#fff' : '#4A7A9B',
              cursor: canImport ? 'pointer' : 'not-allowed',
              boxShadow: canImport ? '0 0 20px rgba(0,212,255,0.3)' : 'none'
            }}
          >
            <FileText size={15} />
            {importing ? t('importing') : `${t('importProperties')} ${tab === 'csv' ? parsedRows?.length : manualRows?.filter(r => r?.address?.trim())?.length}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
